import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  MoveHorizontal, 
  Type, 
  DollarSign, 
  MapPin, 
  Star, 
  Phone, 
  Image,
  Info,
  GripVertical,
  X,
  ChevronDown,
  Palette,
  Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Text role options - heading vs details
const TEXT_ROLES = {
  heading: { label: "Heading", defaultSize: "large", description: "Main headline text" },
  subheading: { label: "Subheading", defaultSize: "medium", description: "Secondary headline" },
  details: { label: "Details", defaultSize: "small", description: "Supporting information" },
  accent: { label: "Accent", defaultSize: "medium", description: "Highlighted text (different color)" }
};

// All 9 positions on the template
const positions = [
  { id: "top-left", label: "Top Left", row: 0, col: 0 },
  { id: "top-center", label: "Top Center", row: 0, col: 1 },
  { id: "top-right", label: "Top Right", row: 0, col: 2 },
  { id: "center-left", label: "Center Left", row: 1, col: 0 },
  { id: "center", label: "Center", row: 1, col: 1 },
  { id: "center-right", label: "Center Right", row: 1, col: 2 },
  { id: "bottom-left", label: "Bottom Left", row: 2, col: 0 },
  { id: "bottom-center", label: "Bottom Center", row: 2, col: 1 },
  { id: "bottom-right", label: "Bottom Right", row: 2, col: 2 },
];

// Fields available per category
const categoryFields = {
  property: [
    { id: "address", label: "Address", icon: MapPin, example: "123 Main St" },
    { id: "price", label: "Price", icon: DollarSign, example: "$899,000" },
    { id: "beds", label: "Beds", icon: Type, example: "4 Beds" },
    { id: "baths", label: "Baths", icon: Type, example: "3 Baths" },
    { id: "sqft", label: "Sq Ft", icon: Type, example: "2,500 sqft" },
    { id: "features", label: "Features", icon: Star, example: "Pool, Garage" },
    { id: "contact", label: "Contact", icon: Phone, example: "Call Agent" },
    { id: "mls", label: "MLS#", icon: Info, example: "MLS# 12345" },
  ],
  product: [
    { id: "name", label: "Product Name", icon: Type, example: "Premium Widget" },
    { id: "price", label: "Price", icon: DollarSign, example: "$49.99" },
    { id: "description", label: "Description", icon: Info, example: "Best quality" },
    { id: "features", label: "Features", icon: Star, example: "Waterproof" },
    { id: "discount", label: "Discount", icon: DollarSign, example: "20% OFF" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Shop Now" },
  ],
  restaurant: [
    { id: "name", label: "Dish Name", icon: Type, example: "Signature Pasta" },
    { id: "price", label: "Price", icon: DollarSign, example: "$24.99" },
    { id: "description", label: "Description", icon: Info, example: "Chef's special" },
    { id: "ingredients", label: "Ingredients", icon: Star, example: "Fresh tomatoes" },
    { id: "calories", label: "Calories", icon: Info, example: "450 cal" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Order Now" },
  ],
  service: [
    { id: "name", label: "Service Name", icon: Type, example: "Premium Cleaning" },
    { id: "price", label: "Starting Price", icon: DollarSign, example: "From $99" },
    { id: "description", label: "Description", icon: Info, example: "Professional service" },
    { id: "benefits", label: "Key Benefits", icon: Star, example: "Eco-friendly" },
    { id: "contact", label: "Contact", icon: Phone, example: "Book Now" },
  ],
  event: [
    { id: "name", label: "Event Name", icon: Type, example: "Summer Festival" },
    { id: "date", label: "Date & Time", icon: Info, example: "June 15, 7PM" },
    { id: "location", label: "Location", icon: MapPin, example: "Central Park" },
    { id: "price", label: "Tickets", icon: DollarSign, example: "From $25" },
    { id: "description", label: "Description", icon: Info, example: "Live music" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Get Tickets" },
  ],
  job: [
    { id: "title", label: "Job Title", icon: Type, example: "Senior Developer" },
    { id: "company", label: "Company", icon: Info, example: "Tech Corp" },
    { id: "location", label: "Location", icon: MapPin, example: "Remote" },
    { id: "salary", label: "Salary", icon: DollarSign, example: "$120k-150k" },
    { id: "benefits", label: "Benefits", icon: Star, example: "Health, 401k" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Apply Now" },
  ],
  automotive: [
    { id: "name", label: "Vehicle", icon: Type, example: "2024 Tesla Model 3" },
    { id: "price", label: "Price", icon: DollarSign, example: "$42,990" },
    { id: "mileage", label: "Mileage", icon: Info, example: "15,000 mi" },
    { id: "features", label: "Features", icon: Star, example: "Autopilot" },
    { id: "condition", label: "Condition", icon: Info, example: "Like New" },
    { id: "contact", label: "Contact", icon: Phone, example: "Test Drive" },
  ],
  fitness: [
    { id: "name", label: "Program/Class", icon: Type, example: "HIIT Training" },
    { id: "price", label: "Price", icon: DollarSign, example: "$50/month" },
    { id: "duration", label: "Duration", icon: Info, example: "45 mins" },
    { id: "benefits", label: "Benefits", icon: Star, example: "Burn 500 cal" },
    { id: "schedule", label: "Schedule", icon: Info, example: "Mon/Wed/Fri" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Join Now" },
  ],
  travel: [
    { id: "destination", label: "Destination", icon: MapPin, example: "Bali, Indonesia" },
    { id: "price", label: "Price", icon: DollarSign, example: "From $999" },
    { id: "duration", label: "Duration", icon: Info, example: "7 Days" },
    { id: "highlights", label: "Highlights", icon: Star, example: "Beach Resort" },
    { id: "dates", label: "Available Dates", icon: Info, example: "June-Aug" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Book Trip" },
  ],
  education: [
    { id: "course", label: "Course Name", icon: Type, example: "Data Science" },
    { id: "price", label: "Price", icon: DollarSign, example: "$299" },
    { id: "duration", label: "Duration", icon: Info, example: "8 Weeks" },
    { id: "level", label: "Level", icon: Star, example: "Beginner" },
    { id: "format", label: "Format", icon: Info, example: "Online" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Enroll Now" },
  ],
  pet: [
    { id: "name", label: "Pet Name/Breed", icon: Type, example: "Golden Retriever" },
    { id: "price", label: "Adoption Fee", icon: DollarSign, example: "$250" },
    { id: "age", label: "Age", icon: Info, example: "2 years" },
    { id: "traits", label: "Traits", icon: Star, example: "Friendly, Trained" },
    { id: "location", label: "Location", icon: MapPin, example: "Pet Shelter" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Adopt Me" },
  ],
  rental: [
    { id: "item", label: "Item Name", icon: Type, example: "Camera Kit" },
    { id: "dailyRate", label: "Daily Rate", icon: DollarSign, example: "$50/day" },
    { id: "weeklyRate", label: "Weekly Rate", icon: DollarSign, example: "$300/week" },
    { id: "deposit", label: "Deposit", icon: DollarSign, example: "$200 deposit" },
    { id: "availability", label: "Availability", icon: Info, example: "Available Now" },
    { id: "cta", label: "Call to Action", icon: Type, example: "Rent Now" },
  ],
};

export default function FieldPositionMapper({
  category,
  fieldValues,
  setFieldValues,
  fieldPositions,
  setFieldPositions,
  template,
  textStyle,
  setTextStyle,
  isAIGenerated = false
}) {
  const [draggedField, setDraggedField] = useState(null);
  const [activePosition, setActivePosition] = useState(null);
  const [showStyleEditor, setShowStyleEditor] = useState(false);
  const [activeFieldForStyling, setActiveFieldForStyling] = useState(null); // For per-field styling
  
  // Default text style (global fallback)
  const defaultTextStyle = {
    fontFamily: "sans-serif",
    fontSize: "medium",
    fontColor: "#ffffff",
    backgroundColor: "rgba(0,0,0,0.7)",
    textAlign: "center"
  };
  
  // Per-field text styles stored in textStyle.fieldStyles
  // Structure: { fieldStyles: { fieldId: { fontSize, fontColor, role } }, ...globalStyles }
  const currentTextStyle = { ...defaultTextStyle, ...textStyle };
  const fieldStyles = currentTextStyle.fieldStyles || {};
  
  const updateTextStyle = (key, value) => {
    if (setTextStyle) {
      setTextStyle({ ...currentTextStyle, [key]: value });
    }
  };
  
  // Update style for a specific field
  const updateFieldStyle = (fieldId, styleKey, value) => {
    if (setTextStyle) {
      const newFieldStyles = {
        ...fieldStyles,
        [fieldId]: {
          ...fieldStyles[fieldId],
          [styleKey]: value
        }
      };
      setTextStyle({ ...currentTextStyle, fieldStyles: newFieldStyles });
    }
  };
  
  // Get style for a specific field (with fallbacks to global)
  const getFieldStyle = (fieldId) => {
    const fs = fieldStyles[fieldId] || {};
    return {
      fontSize: fs.fontSize || currentTextStyle.fontSize,
      fontColor: fs.fontColor || currentTextStyle.fontColor,
      fontFamily: fs.fontFamily || currentTextStyle.fontFamily,
      role: fs.role || "details"
    };
  };
  
  const fields = categoryFields[category] || [];
  
  // Get field value
  const getFieldValue = (fieldId) => fieldValues[fieldId] || "";
  
  // Set field value
  const handleFieldChange = (fieldId, value) => {
    setFieldValues({ ...fieldValues, [fieldId]: value });
  };
  
  // Get fields at position (returns array)
  const getFieldsAtPosition = (positionId) => {
    const fieldsAtPos = fieldPositions[positionId];
    if (!fieldsAtPos) return [];
    // Handle both old format (single string) and new format (array)
    return Array.isArray(fieldsAtPos) ? fieldsAtPos : [fieldsAtPos];
  };
  
  // Assign field to position (allows multiple fields per position)
  const assignToPosition = (fieldId, positionId) => {
    const newPositions = { ...fieldPositions };
    
    // Remove field from any existing position
    Object.keys(newPositions).forEach(pos => {
      const fieldsAtPos = getFieldsAtPosition(pos);
      if (fieldsAtPos.includes(fieldId)) {
        const remaining = fieldsAtPos.filter(f => f !== fieldId);
        if (remaining.length === 0) {
          delete newPositions[pos];
        } else {
          newPositions[pos] = remaining;
        }
      }
    });
    
    // Add to new position
    if (positionId) {
      const existingFields = getFieldsAtPosition(positionId);
      newPositions[positionId] = [...existingFields.filter(f => f !== fieldId), fieldId];
    }
    
    setFieldPositions(newPositions);
  };
  
  // Remove single field from position
  const removeFieldFromPosition = (positionId, fieldId) => {
    const newPositions = { ...fieldPositions };
    const fieldsAtPos = getFieldsAtPosition(positionId).filter(f => f !== fieldId);
    if (fieldsAtPos.length === 0) {
      delete newPositions[positionId];
    } else {
      newPositions[positionId] = fieldsAtPos;
    }
    setFieldPositions(newPositions);
  };
  
  // Remove all fields from position
  const removeFromPosition = (positionId) => {
    const newPositions = { ...fieldPositions };
    delete newPositions[positionId];
    setFieldPositions(newPositions);
  };
  
  // Handle drag start
  const handleDragStart = (e, fieldId) => {
    setDraggedField(fieldId);
    e.dataTransfer.effectAllowed = "move";
  };
  
  // Handle drag over position
  const handleDragOver = (e, positionId) => {
    e.preventDefault();
    setActivePosition(positionId);
  };
  
  // Handle drop on position
  const handleDrop = (e, positionId) => {
    e.preventDefault();
    if (draggedField) {
      assignToPosition(draggedField, positionId);
    }
    setDraggedField(null);
    setActivePosition(null);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setDraggedField(null);
    setActivePosition(null);
  };
  
  // Get position(s) where field is assigned
  const getPositionForField = (fieldId) => {
    for (const [pos, fieldIds] of Object.entries(fieldPositions)) {
      const ids = Array.isArray(fieldIds) ? fieldIds : [fieldIds];
      if (ids.includes(fieldId)) return pos;
    }
    return null;
  };
  
  if (!category) return null;
  
  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <p className="text-sm text-blue-400 flex items-center gap-2">
          <MoveHorizontal className="w-4 h-4" />
          <span>Drag fields to positions. Drop multiple fields on the same tile to combine them!</span>
        </p>
      </div>
      
      {/* Text Style Customization */}
      <div className="mb-4">
        <button
          onClick={() => setShowStyleEditor(!showStyleEditor)}
          className="flex items-center gap-2 text-sm text-viraly-orange hover:text-viraly-orange/80 transition-colors"
        >
          <Type className="w-4 h-4" />
          <span>{showStyleEditor ? "Hide" : "Customize"} Text Style</span>
          <span className="text-xs text-viraly-muted">(Font, Color, Size)</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showStyleEditor ? 'rotate-180' : ''}`} />
        </button>
        
        {showStyleEditor && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Font Family */}
              <div>
                <label className="text-xs text-viraly-muted mb-1 block">Font</label>
                <select
                  value={currentTextStyle.fontFamily}
                  onChange={(e) => updateTextStyle("fontFamily", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm text-white focus:outline-none focus:border-viraly-orange"
                >
                  <option value="sans-serif">Modern (Sans)</option>
                  <option value="serif">Classic (Serif)</option>
                  <option value="Georgia, serif">Elegant</option>
                  <option value="'Courier New', monospace">Technical</option>
                  <option value="Impact, sans-serif">Bold Impact</option>
                </select>
              </div>
              
              {/* Font Size */}
              <div>
                <label className="text-xs text-viraly-muted mb-1 block">Size</label>
                <select
                  value={currentTextStyle.fontSize}
                  onChange={(e) => updateTextStyle("fontSize", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm text-white focus:outline-none focus:border-viraly-orange"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="xlarge">Extra Large</option>
                </select>
              </div>
              
              {/* Font Color */}
              <div>
                <label className="text-xs text-viraly-muted mb-1 block">Text Color</label>
                <div className="flex gap-2">
                  {["#ffffff", "#000000", "#ff6b35", "#fbbf24", "#22c55e", "#3b82f6", "#ec4899"].map(color => (
                    <button
                      key={color}
                      onClick={() => updateTextStyle("fontColor", color)}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        currentTextStyle.fontColor === color 
                          ? "border-viraly-orange scale-110" 
                          : "border-white/30 hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Background Color */}
              <div>
                <label className="text-xs text-viraly-muted mb-1 block">Text Background</label>
                <select
                  value={currentTextStyle.backgroundColor}
                  onChange={(e) => updateTextStyle("backgroundColor", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm text-white focus:outline-none focus:border-viraly-orange"
                >
                  <option value="rgba(0,0,0,0.7)">Dark Overlay</option>
                  <option value="rgba(0,0,0,0.5)">Semi Dark</option>
                  <option value="rgba(255,255,255,0.9)">Light</option>
                  <option value="transparent">None (Transparent)</option>
                  <option value="rgba(255,107,53,0.8)">Orange Brand</option>
                </select>
              </div>
            </div>
            
            {/* Preview */}
            <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: currentTextStyle.backgroundColor }}>
              <p 
                className="text-center"
                style={{ 
                  fontFamily: currentTextStyle.fontFamily,
                  fontSize: currentTextStyle.fontSize === "small" ? "12px" : 
                           currentTextStyle.fontSize === "medium" ? "16px" :
                           currentTextStyle.fontSize === "large" ? "20px" : "24px",
                  color: currentTextStyle.fontColor
                }}
              >
                Preview: Your text will look like this
              </p>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Two Column Layout: Fields | Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Field Inputs */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-viraly-muted mb-2">Enter Your Details</h4>
          {fields.map((field) => {
            const Icon = field.icon;
            const assignedPosition = getPositionForField(field.id);
            const hasValue = !!getFieldValue(field.id);
            const fieldStyle = getFieldStyle(field.id);
            const isEditingStyle = activeFieldForStyling === field.id;
            
            return (
              <div
                key={field.id}
                className={`rounded-xl border transition-all ${
                  assignedPosition 
                    ? "bg-viraly-orange/10 border-viraly-orange/30" 
                    : hasValue
                      ? "bg-green-500/10 border-green-500/30"
                      : "bg-white/5 border-white/10 hover:border-white/20"
                }`}
              >
                {/* Main field row */}
                <div
                  draggable={hasValue}
                  onDragStart={(e) => hasValue && handleDragStart(e, field.id)}
                  onDragEnd={handleDragEnd}
                  className={`p-3 ${hasValue ? "cursor-grab active:cursor-grabbing" : "cursor-default"} ${draggedField === field.id ? "opacity-50" : ""}`}
                  title={hasValue ? "Drag to position on preview" : "Enter a value first to drag"}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-viraly-muted flex-shrink-0" />
                    <Icon className="w-4 h-4 text-viraly-orange flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        placeholder={field.example}
                        value={getFieldValue(field.id)}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-transparent border-none outline-none text-sm placeholder:text-viraly-muted/50"
                      />
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-viraly-muted">{field.label}</span>
                        {assignedPosition && (
                          <span className="text-xs px-2 py-0.5 rounded bg-viraly-orange/20 text-viraly-orange">
                            {positions.find(p => p.id === assignedPosition)?.label}
                          </span>
                        )}
                        {/* Per-field style indicator */}
                        {hasValue && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveFieldForStyling(isEditingStyle ? null : field.id);
                            }}
                            className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                              isEditingStyle 
                                ? "bg-blue-500/30 text-blue-400" 
                                : "bg-white/10 text-viraly-muted hover:bg-white/20"
                            }`}
                          >
                            <Palette className="w-3 h-3" />
                            <span>{TEXT_ROLES[fieldStyle.role]?.label || "Details"}</span>
                          </button>
                        )}
                        {!hasValue && (
                          <span className="text-[10px] text-yellow-500/70 italic">
                            Fill to drag →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Per-field style editor (expandable) */}
                {isEditingStyle && hasValue && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-3 pb-3 border-t border-white/10"
                  >
                    <div className="pt-3 space-y-3">
                      <p className="text-xs text-viraly-muted">Style for "{field.label}":</p>
                      
                      {/* Text Role (Heading/Details) */}
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(TEXT_ROLES).map(([roleId, roleInfo]) => (
                          <button
                            key={roleId}
                            onClick={() => updateFieldStyle(field.id, "role", roleId)}
                            className={`px-2 py-1 rounded text-xs transition-colors ${
                              fieldStyle.role === roleId
                                ? "bg-viraly-orange text-white"
                                : "bg-white/10 text-viraly-muted hover:bg-white/20"
                            }`}
                            title={roleInfo.description}
                          >
                            {roleInfo.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Size & Color Row */}
                      <div className="flex gap-4 items-center flex-wrap">
                        {/* Size */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-viraly-muted">Size:</span>
                          <select
                            value={fieldStyle.fontSize}
                            onChange={(e) => updateFieldStyle(field.id, "fontSize", e.target.value)}
                            className="px-2 py-1 rounded bg-white/10 border border-white/20 text-xs text-white focus:outline-none focus:border-viraly-orange"
                          >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                            <option value="xlarge">X-Large</option>
                          </select>
                        </div>
                        
                        {/* Color */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-viraly-muted">Color:</span>
                          <div className="flex gap-1">
                            {["#ffffff", "#000000", "#ff6b35", "#fbbf24", "#22c55e", "#3b82f6", "#ec4899"].map(color => (
                              <button
                                key={color}
                                onClick={() => updateFieldStyle(field.id, "fontColor", color)}
                                className={`w-5 h-5 rounded-full border transition-all ${
                                  fieldStyle.fontColor === color 
                                    ? "border-viraly-orange scale-110" 
                                    : "border-white/30 hover:scale-105"
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Preview */}
                      <div className="p-2 rounded bg-black/30">
                        <span
                          style={{
                            fontFamily: currentTextStyle.fontFamily,
                            fontSize: fieldStyle.fontSize === "small" ? "12px" : 
                                     fieldStyle.fontSize === "medium" ? "16px" :
                                     fieldStyle.fontSize === "large" ? "20px" : "24px",
                            color: fieldStyle.fontColor,
                            fontWeight: fieldStyle.role === "heading" ? "bold" : 
                                       fieldStyle.role === "subheading" ? "600" : "normal"
                          }}
                        >
                          {getFieldValue(field.id) || field.example}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Right: Position Preview */}
        <div>
          <h4 className="text-sm font-medium text-viraly-muted mb-2">Position Preview</h4>
          <div 
            className="relative aspect-square rounded-xl overflow-hidden border-2 border-dashed border-white/20"
            style={{
              background: template?.background_style?.type === "gradient"
                ? `linear-gradient(135deg, ${template?.background_style?.colors?.join(", ") || "#1a1a2e, #16213e"})`
                : template?.background_style?.color || "#1a1a2e"
            }}
          >
            {/* 3x3 Grid for positions */}
            <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-2">
              {positions.map((pos) => {
                const fieldIds = getFieldsAtPosition(pos.id);
                const positionFields = fieldIds.map(fid => fields.find(f => f.id === fid)).filter(Boolean);
                const isActive = activePosition === pos.id;
                const hasFields = fieldIds.length > 0;
                
                return (
                  <div
                    key={pos.id}
                    onDragOver={(e) => handleDragOver(e, pos.id)}
                    onDrop={(e) => handleDrop(e, pos.id)}
                    onClick={() => {
                      if (draggedField) {
                        assignToPosition(draggedField, pos.id);
                        setDraggedField(null);
                      }
                    }}
                    className={`relative rounded-lg flex flex-col items-center justify-center text-center p-1 transition-all overflow-hidden ${
                      hasFields 
                        ? "bg-viraly-orange/20 border-2 border-viraly-orange"
                        : isActive
                          ? "bg-viraly-orange/10 border-2 border-dashed border-viraly-orange"
                          : "bg-black/20 border-2 border-dashed border-white/20 hover:border-white/40"
                    }`}
                  >
                    {hasFields ? (
                      <div className="w-full space-y-0.5 overflow-hidden">
                        {/* Show styled field values */}
                        <div className="leading-tight">
                          {positionFields.map((field, idx) => {
                            const val = getFieldValue(field.id) || field.example;
                            const fs = getFieldStyle(field.id);
                            const fontSize = fs.fontSize === "small" ? "8px" : 
                                           fs.fontSize === "medium" ? "10px" :
                                           fs.fontSize === "large" ? "12px" : "14px";
                            const fontWeight = fs.role === "heading" ? "bold" : 
                                              fs.role === "subheading" ? "600" : "normal";
                            return (
                              <span 
                                key={field.id}
                                style={{
                                  fontSize,
                                  fontWeight,
                                  color: fs.fontColor
                                }}
                              >
                                {idx > 0 && <span className="mx-0.5 opacity-50" style={{ color: "#ffffff" }}>•</span>}
                                {val}
                              </span>
                            );
                          })}
                        </div>
                        {/* Show field count if multiple */}
                        {fieldIds.length > 1 && (
                          <p className="text-[8px] text-white/50">{fieldIds.length} fields combined</p>
                        )}
                        {/* Clear all button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromPosition(pos.id);
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"
                        >
                          <X className="w-2.5 h-2.5 text-white" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[9px] text-white/40">{pos.label}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-4 text-xs text-viraly-muted">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-viraly-orange/20 border border-viraly-orange"></div>
                <span>Assigned</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-black/20 border border-dashed border-white/20"></div>
                <span>Available</span>
              </div>
            </div>
            <p className="text-[10px] text-viraly-muted/70">
              Tip: Drag multiple fields to the same position to combine them (e.g., "4 Beds • 3 Baths")
            </p>
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFieldPositions({})}
          className="text-xs"
        >
          Clear All Positions
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Auto-assign fields to common positions
            const auto = {};
            fields.forEach((field, idx) => {
              if (idx < positions.length && getFieldValue(field.id)) {
                auto[positions[idx].id] = field.id;
              }
            });
            setFieldPositions(auto);
          }}
          className="text-xs"
        >
          Auto-Assign Filled Fields
        </Button>
      </div>
    </div>
  );
}
